import { Alert, Box, IconButton, Typography, useTheme } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExoAlert from '../../components/exo/ExoAlert';
import ExoForm from '../../components/exo/ExoForm';
import * as Yup from 'yup';
import {
  API_GET,
  API_PATCH,
  API_POST,
  DBRequest,
  useConfig,
} from '../../api/api';
import Feedback from '../../components/special/Feedback';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import MoneyOffCsredIcon from '@mui/icons-material/MoneyOffCsred';
import BabyChangingStationIcon from '@mui/icons-material/BabyChangingStation';
import PregnantWomanIcon from '@mui/icons-material/PregnantWoman';
import FamilyRestroomIcon from '@mui/icons-material/FamilyRestroom';
import MasksIcon from '@mui/icons-material/Masks';
import ElderlyWomanIcon from '@mui/icons-material/ElderlyWoman';
import SchoolIcon from '@mui/icons-material/School';
import StartIcon from '@mui/icons-material/Start';
import KeyboardTabIcon from '@mui/icons-material/KeyboardTab';
import moment from 'moment';
import { tokens } from '../../global/theme/tokens';
import { FreeDay } from './NewTimeCompensation';
import { useTimeTracking } from '../../components/time-tracking/AppTimeProvider';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { TimeAccountCard } from '../time-tracking/TimeAccountCard';
import { useDebounce } from 'use-debounce';

const NewVacation = ({
  onCreate,
  onCancel,
  userId,
  startDataCollection,
  autoAccept,
  existingVacation,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const [timeTrackingData, setTimeTrackingData] = useState(null);

  const timeTrackingProviderData = useTimeTracking();

  useEffect(() => {
    if (userId) {
      setTimeTrackingData(timeTrackingProviderData);
      DBRequest({
        config,
        path: `users/${userId}/time-tracking`,
        method: API_GET,
        onResponse: handleResponse,
        onError: () => setTimeTrackingData(null),
      });
    } else {
      setTimeTrackingData(timeTrackingProviderData);
    }
  }, [config, userId]);

  function handleResponse(data) {
    let newData = {
      ...data.data,
      daysToWork: data.data.daysInWeek,
      vacation: {
        total: data.data.totalVacationDays,
        taken: data.data.takenVacationDays,
      },
    };

    setTimeTrackingData(newData);
  }

  const options = [
    {
      label: t('Vacation'),
      value: 0,
      Icon: BeachAccessIcon,
    },
    {
      label: t('Unpaid vacation'),
      value: 1,
      Icon: MoneyOffCsredIcon,
    },
    {
      label: t('Maternity leave'),
      value: 2,
      Icon: PregnantWomanIcon,
    },
    {
      label: t('Paternity leave'),
      value: 3,
      Icon: BabyChangingStationIcon,
    },
    {
      label: t('Parental leave'),
      value: 4,
      Icon: FamilyRestroomIcon,
    },
    {
      label: t('Child sick leave'),
      value: 5,
      Icon: MasksIcon,
    },
    {
      label: t('Care leave'),
      value: 6,
      Icon: ElderlyWomanIcon,
    },
    {
      label: t('Educational leave'),
      value: 7,
      Icon: SchoolIcon,
    },
  ];

  const fields = [
    {
      Icon: StartIcon,
      label: t('Start'),
      type: 'date',
      key: 'startDate',
    },
    {
      Icon: KeyboardTabIcon,
      label: t('End'),
      type: 'date',
      key: 'endDate',
    },
    {
      label: t('Is half start date'),
      type: 'checkbox',
      key: 'halfStartDate',
    },
    {
      label: t('Is half end date'),
      type: 'checkbox',
      key: 'halfEndDate',
    },
    {
      label: t('type of vacation'),
      type: 'customSelect',
      key: 'type',
      options,
      noUnselect: true,
      default: 0,
      colspan: 2,
    },
    {
      label: t('Comment'),
      type: 'text',
      key: 'comment',
      multiline: true,
      colspan: 2,
    },
  ];

  const validationSchema = Yup.object().shape({
    startDate: Yup.string().required(t('Start Date is required')),
    endDate: Yup.string().required(t('End Date is required')),
  });

  const existingRequestId = existingVacation?.id;

  function handleSubmit(values) {
    if (!workdays) return;

    const baseData = {
      type: 'vacation',
      comment: values.comment,
      values: JSON.stringify({
        ...values,
        minutesPerWeek: timeTrackingData?.minutesPerWeek,
        daysFree: workdays.data.times,
        timeCost: workdays.timeCost,
      }),
    };

    const data = {
      ...baseData,
      ...(userId ? { userId } : {}),
      ...(autoAccept ? { autoAccept: true } : {}),
      ...(existingRequestId ? { id: existingRequestId } : {}),
    };

    DBRequest({
      config,
      path: 'employeerequests/vacation',
      method: API_POST(data),
      onResponse: handleCreation,
      onFeedback: handleAlertOpen,
    });
  }

  function handleCreation(data) {
    onCreate(data.data);
    onCancel();
  }

  const [reqDel, setReqDel] = useState(null);
  function handleDelete() {
    if (!existingRequestId) return;
    DBRequest({
      config,
      path: `employeerequests/${existingRequestId}`,
      method: API_PATCH({ status: 2 }),
      onResponse: handleCreation,
      onFeedback: handleAlertOpen,
    });
  }

  // feedback
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: 'test',
  });
  function handleAlertOpen(text, type) {
    setAlertState({ alertOpen: true, alertText: text, alertType: type });
  }

  // live calculation
  const [diff, setDiff] = useState(0);
  const [workdays, setWorkdays] = useState(null);
  const [dataToDebounce, setDataToDebounce] = useState({});
  const [debouncedData] = useDebounce(dataToDebounce, 700);
  const [error, setError] = useState(null);

  function handleChange(data) {
    const { startDate, endDate, halfStartDate, halfEndDate } = data;

    if (
      (!workdays ||
        startDate !== workdays.startDate ||
        endDate !== workdays.endDate ||
        halfStartDate !== workdays.halfStartDate ||
        halfEndDate !== workdays.halfEndDate) &&
      startDate &&
      endDate
    ) {
      setDataToDebounce(data);
    }
  }

  useEffect(() => {
    if (debouncedData) {
      const { startDate, endDate, halfStartDate, halfEndDate } = debouncedData;
      // validate dates
      const start = moment(startDate);
      const end = moment(endDate);
      if (start.isAfter(end))
        return setError('Start date must be before end date');

      if (end.diff(start, 'days') > 365)
        return setError('Vacation period cannot be longer than 365 days');

      setError(null);
      requestWorkdays(
        startDate,
        endDate,
        parseInt(halfStartDate),
        parseInt(halfEndDate),
      );
    }
  }, [debouncedData]);

  function requestWorkdays(startDate, endDate, halfStartDate, halfEndDate) {
    const formattedStart = moment(startDate).format('YYYY-MM-DD');
    const formattedEnd = moment(endDate).format('YYYY-MM-DD');

    DBRequest({
      config,
      path: userId
        ? `users/${userId}/time-tracking/overview?period=day&startDate=${formattedStart}&endDate=${formattedEnd}`
        : `time-tracking/overview/own?period=day&startDate=${formattedStart}&endDate=${formattedEnd}`,
      onResponse: (data) =>
        handleWorkdaysResponse({
          ...data,
          startDate: formattedStart,
          endDate: formattedEnd,
          halfStartDate,
          halfEndDate,
        }),
    });
  }

  function handleWorkdaysResponse(data) {
    const { halfStartDate, halfEndDate } = data;
    var newTimes = data.data.times;

    var newDiff = 0;

    newTimes.forEach((time, index) => {
      const { totalTimeSeconds, targetTimeSeconds, percentages } = time;
      // startdate and enddate
      var { vacation, sick, holiday, overtime, worked } = percentages;
      const startVacation = vacation;

      const deviation = targetTimeSeconds;
      overtime = 0; // reset overtime

      if (deviation > 0) {
        if (
          (halfStartDate && index === 0) ||
          (halfEndDate && index === newTimes.length - 1)
        ) {
          time.percentages.vacation = 0.5 + startVacation;
        } else {
          time.percentages.vacation = 1 + startVacation;
        }
        // set the isNew flag to true
        time.isNew = true;
      }

      if (vacation > 1) time.percentages.vacation = 1;

      if (time.percentages.vacation - startVacation > 0) {
        newDiff +=
          targetTimeSeconds * (time.percentages.vacation - startVacation);
      }
    });
    setDiff((newDiff / 60) * -1); // convert to minutes and invert
    setWorkdays({
      ...data,
      data: { ...data.data, times: newTimes },
      timeCost: (newDiff / 60) * -1,
    });
  }
  function countDaysWithTargetMinutes(days) {
    if (!days) return 0;
    return days.filter((day) => day.targetTimeSeconds > 0).length;
  }

  // Check if daysToWork is an array or a number and calculate daysPerWeek accordingly
  const daysPerWeek = Array.isArray(timeTrackingData?.daysToWork)
    ? timeTrackingData.daysToWork.length
    : timeTrackingData?.daysToWork;

  const vacationDays = daysPerWeek
    ? (diff / (timeTrackingData?.minutesPerWeek / daysPerWeek)) * -1
    : 0;

  const vacationAvailable =
    timeTrackingData?.vacation.total -
    timeTrackingData?.vacation.taken -
    vacationDays;

  return (
    <Box className="flex flex-col lg:flex-row h-full">
      <Box className="p-4 flex flex-col gap-2 w-full lg:max-w-[600px] lg:h-full">
        <Box
          className="flex items-center justify-between w-full"
          sx={{
            position: 'relative',
            '& .delButton': {
              transition: '0.3s ease',
              bgcolor: colors.error + '30',
              color: colors.error,
              border: `1px solid ${colors.error}80`,
            },
          }}
        >
          <Typography variant="h3" sx={{ textAlign: 'center', width: '100%' }}>
            {existingRequestId ? t('Edit Vacation') : t('New Vacation')}
          </Typography>
          {existingRequestId && (
            <IconButton
              onClick={() => setReqDel(existingVacation)}
              size="small"
              className="delButton"
              sx={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            >
              <DeleteIcon sx={{ fontSize: '18px' }} />
            </IconButton>
          )}
        </Box>
        <Box className="flex justify-center items-center mb-2 gap-2">
          <Box className="p-4">
            <Typography variant="h3">{`${vacationDays} ${t(
              'Vacation Days',
            )}`}</Typography>
            <Typography variant="p">{t('Vacation costs')}</Typography>
          </Box>
          {timeTrackingData?.payType === 1 && (
            <>
              <ArrowForwardIcon />
              <TimeAccountCard
                size="small"
                custom={vacationAvailable}
                minutes={vacationAvailable}
                label={t('Remaining Vacation Days')}
              />
            </>
          )}
        </Box>
        {error && (
          <Box
            className="flex w-full items-center justify-center"
            sx={{
              '.MuiAlert-root': {
                flexDirection: 'row',
              },
            }}
          >
            <Alert severity={'error'} sx={{ width: '100%' }}>
              {error}
            </Alert>
          </Box>
        )}
        <ExoForm
          noDifferences
          fields={fields}
          onSubmit={handleSubmit}
          onCancel={onCancel}
          validationSchema={validationSchema}
          className="grid grid-cols-2 gap-4 pt-2"
          onChange={handleChange}
          startDataCollection={startDataCollection}
        />

        <ExoAlert
          onSubmit={handleDelete}
          open={Boolean(reqDel)}
          onClose={() => setReqDel(null)}
          alert={{
            submitLabel: t('Delete'),
            message: t(
              'Are you sure you want to decline this vacation request?',
            ),
          }}
        />

        <Feedback setState={setAlertState} state={alertState} />
      </Box>
      <Box className="flex flex-col gap-2 py-2 lg:h-full overflow-y-auto w-full">
        <Box
          sx={{ bgcolor: colors.card }}
          className="backdrop-blur-md px-2 py-2 rounded-lg sticky top-0 flex justify-around z-10"
        >
          <Box>
            <Typography fontSize={12} fontWeight={600} className=" opacity-50">
              {t('Total')}
            </Typography>
            <Typography className="monospace">{`${
              workdays?.data.times.length || 0
            } ${
              workdays?.data.times.length > 1 ? t('Days') : t('Day')
            }`}</Typography>
          </Box>
          <Box>
            <Typography fontSize={12} fontWeight={600} className=" opacity-50">
              {t('Workdays')}
            </Typography>
            <Typography className="monospace">{`${countDaysWithTargetMinutes(
              workdays?.data?.times,
            )} ${
              countDaysWithTargetMinutes(workdays?.data?.times) > 1
                ? t('Days')
                : t('Day')
            }`}</Typography>
          </Box>
        </Box>
        {workdays?.data?.times.map((day) => (
          <FreeDay day={day} />
        ))}
      </Box>
    </Box>
  );
};

export default NewVacation;
